/**
 * Pure edits of transcript blocks: split, merge, duplicate, delete, text and
 * speaker changes. Every function returns new objects; blocks it changes are
 * marked as edited.
 */

import { deriveBlockBoundsFromWords } from './wordTimestampSync';

export const generateId = () => {
  return `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const editedCopy = (block, changes) => ({
  ...block,
  ...changes,
  metadata: {
    ...block.metadata,
    isEdited: true,
    lastModified: new Date().toISOString(),
  },
});

export const hasWords = (block) => Array.isArray(block.words) && block.words.length > 0;

export const wordsOrUndefined = (words) => (words && words.length > 0 ? words : undefined);

export const concatWords = (blocks) => blocks.flatMap((block) => (hasWords(block) ? block.words : []));

export const lastOf = (items) => items[items.length - 1];

const wordText = (word) => (word.word || word.raw || '').trim();

const normalizeSpaces = (text) => text.replace(/\s+/g, ' ').trim();

const isBlank = (text) => normalizeSpaces(text).length === 0;

/**
 * Words drift from block.text when the text was edited without re-syncing
 * the words; a few characters of difference is still close enough to cut
 * both at the same place.
 */
const wordsRoughlyMatchText = (block) => {
  const wordsLength = normalizeSpaces(block.words.map(wordText).join(' ')).length;
  return Math.abs(normalizeSpaces(block.text).length - wordsLength) < 3;
};

/**
 * Index of the first word that belongs after `charPosition`, counting the
 * words as joined by single spaces. A word that `charPosition` cuts through
 * goes to the 'next' or the 'previous' part.
 */
const wordIndexAtChar = (words, charPosition, cutWordGoesTo) => {
  let wordStart = 0;
  for (let i = 0; i < words.length; i++) {
    const wordEnd = wordStart + wordText(words[i]).length;
    if (charPosition <= wordStart) return i;
    if (charPosition <= wordEnd) return cutWordGoesTo === 'next' ? i : i + 1;
    wordStart = wordEnd + 1;
  }
  return words.length;
};

/** Same "single space between trimmed words" convention as the DOM render. */
const textFromWords = (words) => words.map(wordText).filter(Boolean).join(' ');

const startOf = (words, fallback) => (words[0]?.start != null ? words[0].start : fallback);

const endOf = (words, fallback) => {
  const last = lastOf(words);
  return last?.end != null ? last.end : fallback;
};

/**
 * Split a block at the cursor into two blocks with the same speaker.
 * @returns {Array} [firstBlock, secondBlock]
 */
export const splitBlock = (block, cursorPosition) => {
  const textBefore = block.text.substring(0, cursorPosition).trim();
  const textAfter = block.text.substring(cursorPosition).trim();

  let splitTime = (block.start + block.end) / 2;
  let wordsBefore;
  let wordsAfter;
  if (hasWords(block) && (isBlank(block.text) || wordsRoughlyMatchText(block))) {
    const splitIndex = wordIndexAtChar(block.words, cursorPosition, 'previous');
    wordsBefore = block.words.slice(0, splitIndex);
    wordsAfter = block.words.slice(splitIndex);
    if (lastOf(wordsBefore)?.end) splitTime = lastOf(wordsBefore).end;
    if (wordsAfter[0]?.start) splitTime = wordsAfter[0].start;
  }

  const firstBlock = editedCopy(block, {
    text: textBefore,
    end: splitTime,
    words: wordsOrUndefined(wordsBefore),
  });
  const secondBlock = editedCopy(block, {
    id: generateId(),
    text: textAfter,
    start: splitTime,
    words: wordsOrUndefined(wordsAfter),
    speaker: block.speaker,
  });
  return [firstBlock, secondBlock];
};

const isValidWordRange = (range, wordCount) =>
  range &&
  Number.isInteger(range.start) &&
  Number.isInteger(range.end) &&
  range.start >= 0 &&
  range.end > range.start &&
  range.end <= wordCount;

/**
 * Cut block.words into the words before, inside and after a selection, or
 * return null when the words cannot be trusted to line up with block.text.
 */
const selectionWordParts = (block, selectionStart, selectionEnd, wordArrayIndices) => {
  if (!hasWords(block)) return null;
  const roughMatch = wordsRoughlyMatchText(block);
  if (!roughMatch && !isBlank(block.text)) return null;

  const { words } = block;
  let selectedStart;
  let selectedEnd;
  if (isValidWordRange(wordArrayIndices, words.length)) {
    selectedStart = wordArrayIndices.start;
    selectedEnd = wordArrayIndices.end;
  } else if (roughMatch) {
    selectedStart = wordIndexAtChar(words, selectionStart, 'next');
    selectedEnd = wordIndexAtChar(words, selectionEnd, 'previous');
  } else {
    selectedStart = 0;
    selectedEnd = 0;
  }
  return {
    before: words.slice(0, selectedStart),
    selected: words.slice(selectedStart, selectedEnd),
    after: words.slice(selectedEnd),
  };
};

/**
 * Split a block by a selection into before / selected / after blocks; the
 * selected part gets `newSpeaker`. Empty parts are left out.
 */
export const splitBlockBySelection = (
  block,
  selectionStart,
  selectionEnd,
  newSpeaker,
  wordArrayIndices = null,
) => {
  const midTime = (block.start + block.end) / 2;
  const wordParts = selectionWordParts(block, selectionStart, selectionEnd, wordArrayIndices);

  // With trusted words, the text comes from the same word slices so a one
  // character drift in block.text cannot leave a stray letter in a neighbour.
  const parts = wordParts
    ? {
        before: { text: textFromWords(wordParts.before), end: endOf(wordParts.before, midTime) },
        selected: {
          text: textFromWords(wordParts.selected),
          start: startOf(wordParts.selected, midTime),
          end: endOf(wordParts.selected, midTime),
        },
      }
    : {
        before: { text: block.text.substring(0, selectionStart), end: midTime },
        selected: { text: block.text.substring(selectionStart, selectionEnd), start: midTime, end: midTime },
      };
  parts.after = wordParts
    ? { text: textFromWords(wordParts.after), start: startOf(wordParts.after, parts.selected.end) }
    : { text: block.text.substring(selectionEnd), start: midTime };

  const beforeBlock = editedCopy(block, {
    text: parts.before.text,
    end: parts.before.end,
    words: wordsOrUndefined(wordParts?.before),
  });
  const selectedBlock = editedCopy(block, {
    id: generateId(),
    text: parts.selected.text,
    start: parts.selected.start,
    end: parts.selected.end,
    words: wordsOrUndefined(wordParts?.selected),
    speaker: newSpeaker,
  });
  const afterBlock = editedCopy(block, {
    id: generateId(),
    text: parts.after.text,
    start: parts.after.start,
    words: wordsOrUndefined(wordParts?.after),
    speaker: block.speaker,
  });

  return [beforeBlock, selectedBlock, afterBlock].filter((part) => part.text.trim().length > 0);
};

export const mergeBlocks = (block1, block2) =>
  editedCopy(block1, {
    text: `${block1.text} ${block2.text}`.trim(),
    end: block2.end,
    words: wordsOrUndefined(concatWords([block1, block2])),
  });

export const reorderBlocks = (blocks, fromIndex, toIndex) => {
  const result = Array.from(blocks);
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  return result.map((block) => editedCopy(block, {}));
};

export const deleteBlock = (blocks, blockId) => {
  return blocks.filter((block) => block.id !== blockId);
};

export const duplicateBlock = (block) =>
  editedCopy(block, { id: generateId(), text: `${block.text} (copy)` });

/**
 * When timed words come along, the block's own start/end are re-derived from
 * them: deleting the first (or last) words of a block must move the block
 * boundary, otherwise the timestamp in the header and every seek keep using
 * audio the block no longer contains.
 *
 * @param {Array|null} syncedWords - Words already synced with `newText`
 */
export const updateBlockText = (blocks, blockId, newText, syncedWords = null) => {
  return blocks.map((block) => {
    if (block.id !== blockId) return block;

    const bounds = syncedWords
      ? deriveBlockBoundsFromWords(syncedWords, block.start, block.end)
      : { start: block.start, end: block.end };

    return editedCopy(block, {
      text: newText,
      ...(syncedWords && { words: syncedWords }),
      start: bounds.start,
      end: bounds.end,
    });
  });
};

export const updateBlockSpeaker = (blocks, blockId, newSpeaker) =>
  updateMultipleBlocksSpeaker(blocks, [blockId], newSpeaker);

export const updateMultipleBlocksSpeaker = (blocks, blockIds, newSpeaker) => {
  const ids = new Set(blockIds);
  return blocks.map((block) => (ids.has(block.id) ? editedCopy(block, { speaker: newSpeaker }) : block));
};
